import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface KeyMetricsCardProps {
  totalAreaSqFt: number;
  totalSquares: number;
  wasteSquares: number;
  avgPitch: string;
  complexityScore: number | string;
  facetCount: number;
}

export const KeyMetricsCard = ({
  totalAreaSqFt,
  totalSquares,
  wasteSquares,
  avgPitch,
  complexityScore,
  facetCount,
}: KeyMetricsCardProps) => {
  const getComplexityLabel = (score: number | string) => {
    if (typeof score === 'string') return score;
    return score <= 2 ? 'Simple' : score <= 3 ? 'Moderate' : 'High';
  };

  const getComplexityColor = (score: number | string) => {
    const label = typeof score === 'string' ? score : getComplexityLabel(score);
    switch (label.toLowerCase()) {
      case 'simple':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'moderate':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
      case 'high':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      default:
        return 'bg-muted text-foreground';
    }
  };

  return (
    <TooltipProvider>
      <Card className="h-full">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Key Metrics</h3>
            <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-500/20">
              Satellite Measured
            </Badge>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Area</span>
              <Badge variant="secondary" className="font-mono text-base">
                {totalAreaSqFt.toLocaleString()} ft²
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total Squares</span>
              <Badge variant="outline" className="font-mono text-base">
                {Math.ceil(totalSquares)}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Waste Squares</span>
              <Badge variant="outline" className="font-mono text-yellow-700">
                {Math.ceil(wasteSquares)}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Average Pitch</span>
              <Badge variant="secondary" className="font-mono font-bold">
                {avgPitch}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Complexity</span>
              <Badge className={getComplexityColor(complexityScore)}>
                {getComplexityLabel(complexityScore)}
              </Badge>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Facets</span>
              <Badge variant="outline">
                {facetCount}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
